'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Controller, useFormContext } from 'react-hook-form';

import { cn } from '@/lib/utils';
import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { FieldTriggerLabel } from '../ui/field-trigger';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Icon } from '../ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// Canonical `.field-trigger` shape (see src/components/ui/field-trigger.tsx):
// 1px border-outline that does NOT grow on focus — focus adds a primary
// border + 2px primary/20 ring instead. Heights follow the 28/32/38
// button-height family (default h-8). The label is a static block label
// ABOVE the trigger (FieldTriggerLabel), not a floating animated label.
const datePickerTriggerVariants = cva(
  [
    'flex w-full items-center justify-between rounded-md border border-outline bg-background px-2.5 text-sm text-foreground',
    'transition-[border-color,box-shadow,background-color] duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)]',
    'hover:border-foreground',
    'focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20',
    'data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/20',
    'disabled:bg-muted disabled:text-foreground/60 disabled:border-outline-variant disabled:cursor-not-allowed disabled:hover:border-outline-variant',
  ],
  {
    variants: {
      variant: {
        default: '',
        error: [
          'border-error',
          'hover:border-error',
          'focus-visible:border-error',
          'data-[state=open]:border-error',
        ],
      },
      inputSize: {
        default: 'h-8',
        sm: 'h-7',
        lg: 'h-[38px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default',
    },
  },
);

type Props = {
  variant?: VariantProps<typeof datePickerTriggerVariants>['variant'];
  inputSize?: VariantProps<typeof datePickerTriggerVariants>['inputSize'];
  fromYear?: number;
  toYear?: number;
  startMonth?: Date;
  endMonth?: Date;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & GeneralFormProps;

function RHFShadcnDatePicker({
  title,
  name,
  description,
  helperText,
  placeholder,
  onChange: externalOnChange,
  inputSize = 'default',
  required,
  fromYear = 1900,
  toYear = new Date().getFullYear() + 10,
  startMonth,
  endMonth,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  disabled = false,
  className,
}: Props) {
  const { control } = useFormContext();
  const translate = useTranslations();
  const resolvedHelperText = helperText ?? description;
  const resolvedPlaceholder = placeholder ?? translate('common.form.pickDate');

  // Calculate default startMonth and endMonth if not provided
  const defaultStartMonth = startMonth || new Date(fromYear, 0);
  const defaultEndMonth = endMonth || new Date(toYear, 11);

  // Calculate date restrictions
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getDisabledDates = (date: Date) => {
    // Check custom min/max dates first
    if (minDate !== undefined && date < minDate) {
      return true;
    }
    if (maxDate !== undefined && date > maxDate) {
      return true;
    }

    // Check past/future restrictions
    if (disablePast === true && date < today) {
      return true;
    }
    if (disableFuture === true && date > today) {
      return true;
    }

    return false;
  };

  const hasTitle = title !== undefined && title !== '';

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const hasValue = field.value !== undefined && field.value !== null && field.value !== '';
        const triggerVariant = fieldState.error !== undefined ? 'error' : 'default';

        return (
          <FormItem className={cn('flex w-full flex-col gap-1.5', className)}>
            {hasTitle === true && (
              <FieldTriggerLabel isError={fieldState.error !== undefined}>
                {title}
                {required === true && <span className="ml-0.5 text-error">*</span>}
              </FieldTriggerLabel>
            )}
            <Popover>
              <PopoverTrigger asChild disabled={disabled}>
                <FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={disabled}
                    aria-invalid={fieldState.error !== undefined ? true : undefined}
                    className={cn(
                      datePickerTriggerVariants({ variant: triggerVariant, inputSize }),
                      'justify-between font-normal',
                      hasValue === false && 'text-on-surface-variant/60',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate text-left">
                      {hasValue === true
                        ? format(parseISO(field.value as string), 'PPP')
                        : resolvedPlaceholder}
                    </span>
                    <Icon
                      icon="lucide:calendar"
                      className="ml-2 shrink-0 text-on-surface-variant"
                      size="xs"
                    />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-md border p-0" align="start">
                <Calendar
                  mode="single"
                  selected={
                    field.value !== undefined && field.value !== null && field.value !== ''
                      ? parseISO(field.value as string)
                      : undefined
                  }
                  onSelect={(date) => {
                    if (date !== undefined) {
                      // Output ISO 8601 datetime format (consistent with backend API expectations)
                      const formattedDate = format(date, 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'');
                      if (externalOnChange !== undefined) {
                        externalOnChange(formattedDate);
                      } else {
                        field.onChange(formattedDate);
                      }
                    }
                  }}
                  disabled={getDisabledDates}
                  captionLayout="dropdown"
                  hideNavigation={true}
                  fromYear={fromYear}
                  toYear={toYear}
                  startMonth={defaultStartMonth}
                  endMonth={defaultEndMonth}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {resolvedHelperText !== undefined && resolvedHelperText !== '' && fieldState.error === undefined && (
              <FormDescription className="mt-1 type-body-s text-on-surface-variant">
                {resolvedHelperText}
              </FormDescription>
            )}
            <FormMessage className="mt-1.5" />
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnDatePicker;
